import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
  useRef,
} from "react";
import dayjs from 'dayjs';
import { CircularProgress, Chip, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { SelectedAlertsContext } from "../../../utils/SelectedAlertContext";
import Multiselectwithsearchinput from "../../components/dropdown/MultiSelectWithInput";
import { ListItem } from "@mui/material";
import SnackBarMessage from "../../components/snackbar/snackbar";
import AlertRmpmKpi from "../../components/Cards/OrltKPI";
import ActionPopup from "../../../Samarth Module/components/popup/actionPopup";
import CustomiseColumns from "../../../Samarth Module/components/drawers/CustomiseColumns";
import MoreFilters from "../../../Samarth Module/components/drawers/moreFilters";

import { getORLTConfig } from "../../constants/orltConfig";
import CustomButton from "../../components/buttons/CustomButton";
import styles from "./orLossTree.module.css";
import { generateKPIDetails } from "utils/generateOrltKPIDetails";
import PropTypes from "prop-types";
import clsx from "clsx";
import resetBtn from "../../../assets/images/resetBtn.png";
import Excel from "../../../assets/images/Excel.svg";
import Filters from "../../../assets/images/filters.svg";
import { useMsal } from "@azure/msal-react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ChatBot from "../../components/chatbot/ChatBot";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import { handleOrLossHeatmapExport, handleWeeklyTrendExport, orHeatMapWeeklyTrendLossTree, orLossExportToExcel, orLossFilters, orLossWeeklyTrendData } from "Samarth Module/api/orltAPI";
import ExportLoader from "Samarth Module/components/snackbar/exportLoader";
import ORLTExcelPopup from "Samarth Module/components/popup/ORLTExcelPopup";

const FILTER_ORDER = ['Factory', 'BG', 'SmallC', 'Format', 'Site Code', 'CBU', 'Basepack', 'Week', 'Year', 'Data UOM'];

const WEEK_YEAR_REGEX = /^(\d{1,2})\.(\d{4})$/;

const parseWeekYearValue = (value) => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  const match = text.match(WEEK_YEAR_REGEX);
  if (!match) return null;

  const week = Number(match[1]);
  const year = Number(match[2]);

  if (Number.isNaN(week) || Number.isNaN(year)) return null;
  return { week, year, token: `${week}.${year}` };
};

const normalizeWeekYearToken = (week, year) => {
  if (week === undefined || week === null || year === undefined || year === null) return null;
  const weekNum = Number(String(week).trim());
  const yearNum = Number(String(year).trim());
  if (Number.isNaN(weekNum) || Number.isNaN(yearNum)) return null;
  return `${weekNum}.${yearNum}`;
};

const sortWeekYearDesc = (values = []) => {
  return [...values].sort((first, second) => {
    const parsedFirst = parseWeekYearValue(first);
    const parsedSecond = parseWeekYearValue(second);

    if (parsedFirst && parsedSecond) {
      if (parsedSecond.year !== parsedFirst.year) return parsedSecond.year - parsedFirst.year;
      return parsedSecond.week - parsedFirst.week;
    }

    const firstNum = Number(first);
    const secondNum = Number(second);
    if (!Number.isNaN(firstNum) && !Number.isNaN(secondNum)) {
      return secondNum - firstNum;
    }

    return String(second).localeCompare(String(first));
  });
};

const buildWeekOptions = (weekValues = [], yearValues = [], selectedYearValues = []) => {
  const uniqueWeeks = [...new Set((weekValues || []).filter((value) => value !== undefined && value !== null))];
  if (!uniqueWeeks.length) return [];

  const combinedFromWeek = uniqueWeeks
    .map((value) => parseWeekYearValue(value)?.token)
    .filter(Boolean);

  if (combinedFromWeek.length) {
    return sortWeekYearDesc([...new Set(combinedFromWeek)]);
  }

  const normalizedWeeks = uniqueWeeks
    .map((value) => String(value).trim())
    .filter(Boolean);

  const uniqueSelectedYears = [...new Set(
    (selectedYearValues || [])
      .filter((value) => value !== undefined && value !== null)
      .map((value) => String(value).trim())
      .filter(Boolean)
  )];

  // Only build week.year when year choice is unambiguous; avoid creating invalid combinations.
  if (uniqueSelectedYears.length === 1) {
    const year = uniqueSelectedYears[0];
    return sortWeekYearDesc(
      [...new Set(normalizedWeeks.map((week) => normalizeWeekYearToken(week, year)).filter(Boolean))]
    );
  }

  return sortWeekYearDesc(normalizedWeeks);
};

const getFiltersWithoutWeekSelection = (filters = []) => {
  if (!Array.isArray(filters)) return [];
  return filters.map((filter) =>
    filter?.label === "Week" ? { ...filter, selectedData: [] } : filter
  );
};

const getFirstNonEmptyValue = (item = {}, keys = []) => {
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return undefined;
};

const getWeekYearTokenFromAlertId = (alertIdValue) => {
  const alertId = String(alertIdValue ?? "").trim();
  if (!alertId) return null;

  const fiscalYearWeekMatch = alertId.match(/(?:FY)?(\d{2,4})\D{0,4}W?(\d{1,2})/i);
  if (fiscalYearWeekMatch) {
    const rawYear = fiscalYearWeekMatch[1];
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    return normalizeWeekYearToken(fiscalYearWeekMatch[2], year);
  }

  const yearWeekMatch = alertId.match(/(\d{4})[-_.](\d{1,2})/);
  if (yearWeekMatch) {
    return normalizeWeekYearToken(yearWeekMatch[2], yearWeekMatch[1]);
  }

  const weekYearMatch = alertId.match(/(\d{1,2})[-_.](\d{4})/);
  if (weekYearMatch) {
    return normalizeWeekYearToken(weekYearMatch[1], weekYearMatch[2]);
  }

  return null;
};

const splitAndTrimValues = (value) => {
  if (value === undefined || value === null) return [];
  if (typeof value === "string" && value.includes(",")) {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [value];
};

const hasWeekYearFormattedValue = (values = []) => {
  for (const value of values) {
    if (WEEK_YEAR_REGEX.test(String(value).trim())) {
      return true;
    }
  }
  return false;
};

const getExistingWeekYearOptions = (values = []) =>
  (values || [])
    .map((value) => String(value).trim())
    .filter((value) => WEEK_YEAR_REGEX.test(value));

const normalizeSelectedValues = (selectedData = []) =>
  (selectedData || []).map((value) => String(value).trim());

const toSelectedValueArray = (selectedData) => {
  if (Array.isArray(selectedData)) return selectedData;
  if (selectedData === undefined || selectedData === null) return [];
  if (typeof selectedData === "string" && selectedData.trim() === "") return [];
  return [selectedData];
};

const hasSelectedFilterValues = (filter) =>
  toSelectedValueArray(filter?.selectedData).length > 0;

const toComparableWeekValues = (rowWeekValue) => {
  const weekText = String(rowWeekValue).trim();
  const weekNumber = Number(weekText);
  if (Number.isNaN(weekNumber)) {
    return [weekText];
  }
  return [weekText, String(weekNumber)];
};

const matchesWeekSelection = (item, selectedData = []) => {
  const selectedValues = normalizeSelectedValues(selectedData);
  const hasWeekYearSelection = hasWeekYearFormattedValue(selectedValues);

  if (hasWeekYearSelection) {
    const selectedTokens = selectedValues
      .map((value) => parseWeekYearValue(value)?.token)
      .filter(Boolean);

    if (!selectedTokens.length) return true;
    const rowToken = getItemWeekYearToken(item);
    return rowToken ? selectedTokens.includes(rowToken) : false;
  }

  const rowWeek = item.Week ?? item["Week"];
  if (rowWeek === undefined || rowWeek === null) return false;
  const comparableWeekValues = toComparableWeekValues(rowWeek);
  return comparableWeekValues.some((value) => selectedValues.includes(value));
};

const matchesGenericSelection = (itemValue, selectedData = []) => {
  if (itemValue === undefined || itemValue === null) return false;
  const selectedValues = toSelectedValueArray(selectedData);
  if (!selectedValues.length) return true;
  const candidateValues = splitAndTrimValues(itemValue);

  for (const candidate of candidateValues) {
    if (selectedValues.includes(candidate)) {
      return true;
    }
  }

  return false;
};

const matchesItemByFilter = (item, filter, weekAware = false) => {
  if (!hasSelectedFilterValues(filter)) return true;

  if (weekAware && filter.label === "Week") {
    return matchesWeekSelection(item, filter.selectedData);
  }

  return matchesGenericSelection(item[filter.label], filter.selectedData);
};

const getDistinctFilterValuesFromRows = (rows = [], label) => {
  const allValues = [];
  for (const item of rows) {
    const values = splitAndTrimValues(item?.[label]);
    allValues.push(...values);
  }
  return [...new Set(allValues)].sort((first, second) =>
    String(first).localeCompare(String(second))
  );
};

const findFilterByLabel = (filters = [], label) => {
  for (const filter of filters) {
    if (filter?.label === label) {
      return filter;
    }
  }
  return null;
};

const getFilterOptionValues = (filter = {}, existingFilter = null) => {
  const hasSelection = hasSelectedFilterValues(existingFilter);
  return hasSelection
    ? (filter.selectedData ?? filter.data ?? [])
    : (filter.data ?? []);
};

const resolveWeekOptions = ({
  weekOptionsResponse = [],
  values = [],
  yearOptions = [],
  selectedYearValues = [],
  weekOptionsFromApis = [],
  existingWeekYearOptions = [],
}) => {
  const baseWeekOptions = weekOptionsResponse.length ? weekOptionsResponse : values;
  const computedWeekOptions = weekOptionsFromApis.length
    ? weekOptionsFromApis
    : buildWeekOptions(baseWeekOptions, yearOptions, selectedYearValues);

  if (hasWeekYearFormattedValue(computedWeekOptions)) {
    return computedWeekOptions;
  }

  return existingWeekYearOptions.length ? existingWeekYearOptions : computedWeekOptions;
};

const buildUpdatedFilterOptions = ({
  prevFilters = [],
  prevOptions = {},
  responseFilters = [],
  weekOptionsResponse = [],
  weekOptionsFromApis = [],
}) => {
  const updatedOptions = { ...prevOptions };
  const yearFilterFromResponse = findFilterByLabel(responseFilters, "Year");
  const selectedYearValues = findFilterByLabel(prevFilters, "Year")?.selectedData ?? [];
  const yearOptions = yearFilterFromResponse?.data ?? updatedOptions["Year"] ?? [];
  const existingWeekYearOptions = getExistingWeekYearOptions(updatedOptions["Week"] ?? []);

  for (const filter of responseFilters) {
    if (!filter?.label) continue;

    const existingFilter = findFilterByLabel(prevFilters, filter.label);
    const values = getFilterOptionValues(filter, existingFilter);

    if (filter.label === "Week") {
      updatedOptions[filter.label] = resolveWeekOptions({
        weekOptionsResponse,
        values,
        yearOptions,
        selectedYearValues,
        weekOptionsFromApis,
        existingWeekYearOptions,
      });
      continue;
    }

    updatedOptions[filter.label] = values;
  }

  updatedOptions["Data UOM"] = ["RPM", "CLD"];
  return updatedOptions;
};

const removeSelectionAtIndex = (selectedData, indexToRemove) => {
  if (!Array.isArray(selectedData)) return [];
  const result = [];
  for (let index = 0; index < selectedData.length; index += 1) {
    if (index !== indexToRemove) {
      result.push(selectedData[index]);
    }
  }
  return result;
};

const getItemWeekYearToken = (item = {}) => {
  const directWeekYearKeys = ["CalendarWeek", "Calendar Week", "Week.Year", "Week_Year", "Week"];
  for (const key of directWeekYearKeys) {
    const parsed = parseWeekYearValue(item[key]);
    if (parsed) return parsed.token;
  }

  const yearCandidateKeys = ["Year", "CalendarYear", "Calendar Year", "FiscalYear", "Fiscal Year", "FY"];
  const yearCandidate = getFirstNonEmptyValue(item, yearCandidateKeys);

  const parsedWeekOnly = Number(item.Week ?? item["Week"]);
  if (!Number.isNaN(parsedWeekOnly) && yearCandidate !== undefined) {
    const token = normalizeWeekYearToken(parsedWeekOnly, yearCandidate);
    if (token) return token;
  }

  const fromWeekYear = normalizeWeekYearToken(item.Week ?? item["Week"], item.Year ?? item["Year"]);
  if (fromWeekYear) return fromWeekYear;

  const alertIdToken = getWeekYearTokenFromAlertId(item["Alert ID"]);
  if (alertIdToken) return alertIdToken;

  return null;
};

const getCalendarWeeksFromBackendRows = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];

  const tokens = rows
    .map((row) => getItemWeekYearToken(row))
    .filter(Boolean);

  return sortWeekYearDesc([...new Set(tokens)]);
};

const getCalendarWeeksFromTrendRows = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];

  const tokens = rows
    .map((row) => {
      const parsed = parseWeekYearValue(row?.CalendarWeek);
      if (parsed) return parsed.token;
      const text = String(row?.CalendarWeek ?? "").trim();
      return WEEK_YEAR_REGEX.test(text) ? text : null;
    })
    .filter(Boolean);

  return sortWeekYearDesc([...new Set(tokens)]);
};

const getCalendarWeeksFromHeatMapRows = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];

  const tokens = rows
    .map((row) => {
      if (String(row?.CalendarWeek ?? "").trim() === "Total") return null;
      const parsed = parseWeekYearValue(row?.CalendarWeek);
      if (parsed) return parsed.token;
      const text = String(row?.CalendarWeek ?? "").trim();
      return WEEK_YEAR_REGEX.test(text) ? text : null;
    })
    .filter(Boolean);

  return sortWeekYearDesc([...new Set(tokens)]);
};

const fetchWeekOptionsFromApis = async (filterValues = [], viewType = "") => {
  const weekOptionsFilters = getFiltersWithoutWeekSelection(filterValues);

  const [trendResponse, heatMapResponse] = await Promise.all([
    orLossWeeklyTrendData(weekOptionsFilters, viewType, 'Week').catch(() => []),
    orHeatMapWeeklyTrendLossTree("CalendarWeek", "Loss 1", weekOptionsFilters, viewType).catch(() => ({})),
  ]);

  const trendRows = Array.isArray(trendResponse)
    ? trendResponse
    : (Array.isArray(trendResponse?.data) ? trendResponse.data : []);
  const heatMapRows = Array.isArray(heatMapResponse?.Loss1) ? heatMapResponse.Loss1 : [];

  const weekOptionsFromHeatMap = getCalendarWeeksFromHeatMapRows(heatMapRows);
  if (weekOptionsFromHeatMap.length) return weekOptionsFromHeatMap;

  return getCalendarWeeksFromTrendRows(trendRows);
};

const ORLossTree = ({ alertType }) => {
  const {
    kpiDetails: initialKpiDetails,
    uiConfig,
    apiConfig,
  } = getORLTConfig(alertType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const kpiDetails = useMemo(() => initialKpiDetails, [alertType]);
  const shouldUseContext = [
    "RMPM Alert",
    "Agile OR",
    "Contract Alert",
  ].includes(alertType);
  const { accounts } = useMsal();
  const activeAccount = accounts[0] || null;

  const {
    alertDetailsData,
    selectedAlertAndRecomm,
    orLossTreeSelectedFilters,
    setOrLossTreeSelectedFilters
  } = useContext(SelectedAlertsContext);

  const [state, setState] = useState({
    loading: true,
    drawerState: false,
    isDrawerOpen: false,
    filterDrawer: false,
    openPopup: false,
    filteredData: [],
    maiDetailsData: [],
    maiFilteredData: [],
    maiDetailsColumns: [],
    maiFilters: [],
    rmpmFilters: [],
    excelLoading: false,
    p2AlertCount: 0,
    defaultColumns: [],
    columnList: [],
    ContractAvailableCount: 0,
    OtherRootCauseCount: 0,
    supplierInsights: [],
    vendorQCIndex: [],
    overduePO: [],
    leadTimeAnalysis: [],
    sobCompliance: [],
    weekNum: [],
    segmentationCounts: 0,
    priorityCounts: 0,
    orLossTreeKPIData: {},
    rmpmFiltersOption: {},
    openExcelPopup: false,
  });

  const [viewType, setViewType] = useState(
    uiConfig.showToogleButton ? uiConfig.toggleBtn[0] : ""
  );
  const [snackBarObject, setSnackBarObject] = useState({
    snackbarOpen: false,
    message: "",
    type: "error",
  });
  const [exportLoader, setExportLoader] = useState(false);
  const [filterChangeCount, setFilterChangeCount] = useState(0);


  const latestFiltersRef = useRef([]);
  const cascadeTimerRef = useRef(null);
  const filterInitializedRef = useRef(false);
  const clearFiltersRef = useRef(false);

  const alertDetails = useCallback(() => {
    return shouldUseContext ? alertDetailsData : state.alertDetailsData;
  }, [shouldUseContext, alertDetailsData, state.alertDetailsData]);

  const selectedRecomLength = useMemo(
    () =>
      selectedAlertAndRecomm?.reduce(
        (acc, item) => acc + (item?.selectedRecom?.length || 0),
        0
      ),
    [selectedAlertAndRecomm]
  );

  const filteredDataStr = JSON.stringify(state.filteredData);
  const kpiData = useMemo(() => {
    if (!uiConfig.showKPI) return null;
    return generateKPIDetails({
      kpiDetails,
      filteredData: state.filteredData,
      p2AlertCount: state.p2AlertCount,
      alertType,
      orLossTreeKPIData: state.orLossTreeKPIData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiDetails, state.orLossTreeKPIData, state.p2AlertCount, filteredDataStr]);

  const TableComponent = useMemo(
    () => apiConfig.tableComponent,
    [apiConfig.tableComponent]
  );
  const TableComponent2 = useMemo(
    () => apiConfig.tableComponent2,
    [apiConfig.tableComponent2]
  );

  const isWeekMultiple = useMemo(() => {
    const weekItem = Array.isArray(orLossTreeSelectedFilters) ? orLossTreeSelectedFilters.find(item => item.label === "Week") : {};
    const weekLength = weekItem && Array.isArray(weekItem.selectedData) ? weekItem.selectedData.length : 0;
    return weekLength > 1
  }, [orLossTreeSelectedFilters])

  const fetchData = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      if (!apiConfig.apiFunction) {
        throw new Error(`No API function defined for alert type: ${alertType}`);
      }

      if (alertType === 'Or Loss Tree') {
        const shouldClearFilters = clearFiltersRef.current;
        clearFiltersRef.current = false;
        
        const [data, weekOptionsFromApis] = await Promise.all([
          apiConfig.apiFunction(orLossTreeSelectedFilters, viewType, shouldClearFilters),
          fetchWeekOptionsFromApis(orLossTreeSelectedFilters, viewType),
        ]);

        setState((prev) => {
          let filters = prev.rmpmFilters?.length
            ? prev.rmpmFilters
            : (Array.isArray(data) ? data.map((obj) => {
              return { ...obj, selectedData: [] }
            }) : []);

          // Add Data UOM filter if not present
          const modeFilterExists = filters.some(f => f.label === 'Data UOM');
          if (!modeFilterExists && filters.length > 0) {
            filters = [
              ...filters,
              {
                label: 'Data UOM',
                data: ['RPM', 'CLD'],
                selectedData: 'CLD',
                isFavourite: true,
                isMultiSelect: false
              },
            ];
          }

          const yearFilter = filters.find((filter) => filter.label === 'Year');
          const weekFilterIndex = filters.findIndex((filter) => filter.label === 'Week');
          if (weekFilterIndex !== -1) {
            const weekOptionsFromBackend = buildWeekOptions(
              filters[weekFilterIndex].data,
              yearFilter?.data,
              yearFilter?.selectedData
            );
            const resolvedWeekOptions = weekOptionsFromApis.length
              ? weekOptionsFromApis
              : weekOptionsFromBackend;

            filters[weekFilterIndex] = {
              ...filters[weekFilterIndex],
              data: resolvedWeekOptions
            };
          }

          filters.sort((a, b) =>
            (FILTER_ORDER.indexOf(a.label) === -1 ? 999 : FILTER_ORDER.indexOf(a.label)) -
            (FILTER_ORDER.indexOf(b.label) === -1 ? 999 : FILTER_ORDER.indexOf(b.label))
          );

          const initialOptions = {};
          filters.forEach(f => { if (f.data) initialOptions[f.label] = f.data; });

          latestFiltersRef.current = filters;

          return {
            ...prev,
            rmpmFilters: filters,
            rmpmFiltersOption: initialOptions,
            loading: false
          }
        });

        if (!orLossTreeSelectedFilters.some(f => f.label === 'Data UOM')) {
          setOrLossTreeSelectedFilters(prev => [
            ...prev,
            { label: 'Data UOM', selectedData: 'CLD' }
          ]);
        }

        // If user/context has no Year selection, default the UI selection to 2026
        if (!orLossTreeSelectedFilters.some(f => f.label === 'Year')) {
          const yearFilterExists = latestFiltersRef.current?.some(f => f.label === 'Year');
          if (yearFilterExists) {
            const currentYear = String(dayjs().year());
            setOrLossTreeSelectedFilters(prev => [
              ...prev,
              { label: 'Year', selectedData: [currentYear] }
            ]);

            setState(prev => ({
              ...prev,
              rmpmFilters: prev.rmpmFilters.map(f =>
                f.label === 'Year' ? { ...f, selectedData: [currentYear] } : f
              )
            }));
          }
        }

        filterInitializedRef.current = true;
        return;
      }
    } catch (error) {
      setSnackBarObject({
        snackbarOpen: true,
        message:
          "An error occurred while fetching data. Please refresh or try again",
        type: "error",
      });
      setState((prev) => ({ ...prev, loading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertType, apiConfig.apiFunction]);

  const fetchkpiData = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      if (!apiConfig.apiKPICard) {
        throw new Error(`No API function defined for alert type: ${alertType}`);
      }

      if (alertType === 'Or Loss Tree') {
        const [data, weekOptionsFromApis] = await Promise.all([
          apiConfig.apiKPICard(orLossTreeSelectedFilters, viewType),
          fetchWeekOptionsFromApis(orLossTreeSelectedFilters, viewType),
        ]);
        const weekOptionsFromBackend = getCalendarWeeksFromBackendRows(data?.alertDetails || []);
        const resolvedWeekOptions = weekOptionsFromApis.length
          ? weekOptionsFromApis
          : weekOptionsFromBackend;

        setState((prev) => ({
          ...prev,
          filteredData: data?.alertDetails || [],
          rmpmFiltersOption: resolvedWeekOptions.length
            ? { ...prev.rmpmFiltersOption, Week: resolvedWeekOptions }
            : prev.rmpmFiltersOption,
          orLossTreeKPIData: {
            OR_Percentage: data?.OR_Percentage || "0",
            OR_Percentage_Change: data?.["OR_Percentage_Change(%)"] || 0,
            ProductionPlan: data?.ProductionPlan || "0",
            ProductionPlan_Change: data?.["ProductionPlan_Change(%)"] || 0,
            OR_Loss: data?.OR_Loss || "0",
            OR_Loss_Change: data?.["OR_Loss_Change(%)"] || 0,
            Week: data?.Week || "N/A"
          },
          loading: false
        }));
        return;
      }
    } catch (error) {
      setSnackBarObject({
        snackbarOpen: true,
        message:
          "An error occurred while fetching data. Please refresh or try again",
        type: "error",
      });
      setState((prev) => ({ ...prev, loading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orLossTreeSelectedFilters, alertType, apiConfig.apiKPICard, viewType]);

  useEffect(() => {
    if (viewType !== "MAI View") return;
    if (!state.maiDetailsData.length) return;

    const updatedMAIFilters = state.maiFilters.map((filter) => {
      if (filter.selectedData?.length) {
        return filter;
      }

      const allValues = getDistinctFilterValuesFromRows(state.maiFilteredData, filter.label);

      return {
        ...filter,
        data: allValues,
      };
    });

    const filtersChanged = updatedMAIFilters.some(
      (filter, i) =>
        JSON.stringify(filter) !== JSON.stringify(state.maiFilters[i])
    );

    if (filtersChanged) {
      setState((prev) => ({ ...prev, maiFilters: updatedMAIFilters }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.maiDetailsData, viewType]);

  useEffect(() => {
    if (viewType === "MAI View") return;
    const sourceData = alertDetails();
    if (!sourceData) return;

    const updatedFilteredData = sourceData.filter((item) =>
      state.rmpmFilters.every((filter) => matchesItemByFilter(item, filter, true))
    );

    if (
      JSON.stringify(updatedFilteredData) !== JSON.stringify(state.filteredData)
    ) {
      setState((prev) => ({ ...prev, filteredData: updatedFilteredData }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.rmpmFilters, alertDetailsData, state.alertDetailsData]);

  // Apply filters (MAI view)
  useEffect(() => {
    if (viewType !== "MAI View") return;
    if (!state.maiDetailsData.length) return;

    const updatedFilteredData = state.maiDetailsData.filter((item) =>
      state.maiFilters.every((filter) => matchesItemByFilter(item, filter, false))
    );

    if (
      JSON.stringify(updatedFilteredData) !==
      JSON.stringify(state.maiFilteredData)
    ) {
      setState((prev) => ({ ...prev, maiFilteredData: updatedFilteredData }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.maiFilters, state.maiDetailsData, viewType]);

  const handleMultiSelectChange = useCallback(
    (label, values, isMAI = false) => {
      const filterKey = isMAI ? "maiFilters" : "rmpmFilters";

      setState((prev) => {
        let updated = prev[filterKey].map((filter) =>
          filter.label === label ? { ...filter, selectedData: values } : filter
        );

        // If Year changed, clear Week selection to avoid stale tokens
        if (label === 'Year') {
          updated = updated.map((filter) =>
            filter.label === 'Week' ? { ...filter, selectedData: [] } : filter
          );
        }

        return { ...prev, [filterKey]: updated };
      });

      if (alertType === "Or Loss Tree") {
        latestFiltersRef.current = latestFiltersRef.current.map((filter) =>
          filter.label === label ? { ...filter, selectedData: values } : filter
        );

        // If Year changed, ensure Week is cleared in the latestFiltersRef and context
        if (label === 'Year') {
          latestFiltersRef.current = latestFiltersRef.current.map((filter) =>
            filter.label === 'Week' ? { ...filter, selectedData: [] } : filter
          );
        }

        setOrLossTreeSelectedFilters((prevFilters) => {
          let updated = [...prevFilters.filter(f => f.label !== label), { label, selectedData: values }];
          if (label === 'Year') {
            // also clear Week in context filters
            updated = updated.map((f) => f.label === 'Week' ? { ...f, selectedData: [] } : f);
          }
          
          // Check if there are any selected filters
          const hasSelection = updated.some((filter) => {
            const selected = filter?.selectedData;
            if (Array.isArray(selected)) return selected.length > 0;
            if (typeof selected === 'string') return selected.trim().length > 0;
            return selected !== undefined && selected !== null;
          });
          
          clearFiltersRef.current = !hasSelection;
          return updated;
        });

        setFilterChangeCount(c => c + 1);
      }
    },
    [alertType, setOrLossTreeSelectedFilters]
  );

  const toggleDrawer = useCallback(
    (open) => () => {
      if (selectedRecomLength === 0) return;
      setState((prev) => ({ ...prev, drawerState: open }));
    },
    [selectedRecomLength]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmit = useCallback(async () => {
    setState((prev) => ({ ...prev, openPopup: true }));
  }, []);

  const handleCustomizeDrawer = useCallback(() => {
    setState((prev) => ({ ...prev, isDrawerOpen: !prev.isDrawerOpen }));
  }, []);

  const columnCustomization = useCallback(
    (list) => {
      const target =
        viewType === "MAI View" ? "maiDetailsColumns" : "columnList";
      setState((prev) => ({ ...prev, [target]: list, isDrawerOpen: false }));
    },
    [viewType]
  );

  const handleFilterDrawer = useCallback(() => {
    setState((prev) => ({ ...prev, filterDrawer: !prev.filterDrawer }));
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackBarObject({ message: "", snackbarOpen: false, type: "error" });
  }, []);

  const handleToggleBtnChange = (event, newViewType) => {
    if (newViewType !== null) {
      setViewType(newViewType);
    }
  };
  const removeStorageItems = () => {
    localStorage.removeItem("or_trend_chart");
    localStorage.removeItem("map_losses");
    localStorage.removeItem("map_losses_filter");
    localStorage.removeItem("or_trend_week")
    localStorage.removeItem("or_line_fill")
  }
  const resetFilter = useCallback(() => {
    clearFiltersRef.current = true;
    removeStorageItems()
    if (viewType === "MAI View") {
      setState((prev) => ({
        ...prev,
        maiFilters: prev.maiFilters.map((filter) => ({
          ...filter,
          selectedData: [],
        })),
        maiFilteredData: prev.maiDetailsData,
      }));
    } else if (alertType === "Or Loss Tree") {
      const updatedContextFilters = orLossTreeSelectedFilters.map((filter) => ({
        label: filter.label,
        selectedData: filter.label === 'Data UOM' ? 'CLD' : filter.label === 'Year' ? [String(dayjs().year())] : [],
      }));
      setOrLossTreeSelectedFilters(updatedContextFilters);

      setState((prev) => ({
        ...prev,
        rmpmFilters: prev.rmpmFilters.map((filter) => ({
          ...filter,
          selectedData: filter.label === 'Data UOM' ? 'CLD' : filter.label === 'Year' ? [String(dayjs().year())] : [],
        })),
        filteredData: alertDetails() || [],
      }));
      setFilterChangeCount(c => c + 1);
    } else {
      setState((prev) => ({
        ...prev,
        rmpmFilters: prev.rmpmFilters.map((filter) => ({
          ...filter,
          selectedData: [],
        })),
        filteredData: alertDetails() || [],
      }));
    }
  }, [
    alertType,
    viewType,
    alertDetails,
    orLossTreeSelectedFilters,
    setOrLossTreeSelectedFilters,
  ]);

  useEffect(() => {
    latestFiltersRef.current = state.rmpmFilters;
  }, [state.rmpmFilters]);

  useEffect(() => {
    removeStorageItems();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertType]);

  useEffect(() => {
    if (!filterInitializedRef.current) return;
    fetchkpiData();
    removeStorageItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orLossTreeSelectedFilters, alertType, viewType]);

  // Cascade: re-fetch narrowed filter options when user changes a filter.
  useEffect(() => {
    if (alertType !== 'Or Loss Tree' || filterChangeCount === 0) return;

    let isMounted = true;
    if (cascadeTimerRef.current) clearTimeout(cascadeTimerRef.current);

    cascadeTimerRef.current = setTimeout(async () => {
      if (!isMounted) return;
      try {
        const shouldClearFilters = clearFiltersRef.current;
        clearFiltersRef.current = false;
        
        const filtersWithoutWeek = getFiltersWithoutWeekSelection(latestFiltersRef.current);

        const [data, weekOptionsResponse, weekOptionsFromApis] = await Promise.all([
          orLossFilters(latestFiltersRef.current, viewType, shouldClearFilters),
          orLossFilters(filtersWithoutWeek, viewType, shouldClearFilters),
          fetchWeekOptionsFromApis(latestFiltersRef.current, viewType),
        ]);

        if (!isMounted || !Array.isArray(data)) return;

        const weekFilterFromWeekOptions = Array.isArray(weekOptionsResponse)
          ? weekOptionsResponse.find((filter) => filter.label === 'Week')
          : null;
        const weekValuesWithoutSelfFilter =
          weekFilterFromWeekOptions?.data ?? weekFilterFromWeekOptions?.selectedData ?? [];

        setState((prev) => {
          if (!prev.rmpmFilters.length) return prev;

          const updatedOptions = buildUpdatedFilterOptions({
            prevFilters: prev.rmpmFilters,
            prevOptions: prev.rmpmFiltersOption,
            responseFilters: data,
            weekOptionsResponse: weekValuesWithoutSelfFilter,
            weekOptionsFromApis,
          });
          return { ...prev, rmpmFiltersOption: updatedOptions };
        });
      } catch (_) { /* cascade failure is non-critical */ }
    }, 300);

    return () => {
      isMounted = false;
      if (cascadeTimerRef.current) clearTimeout(cascadeTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterChangeCount, alertType, viewType]);

  const handleExcelPopup = () =>{
    setState((prev) => ({ ...prev, openExcelPopup: true }));
  };

  const handleExportToExcel = async (isRM_table) => {
    setState((prev) => ({ ...prev, openExcelPopup: false }));
    setExportLoader(true);
    try {
      await orLossExportToExcel({ filterValues: orLossTreeSelectedFilters, view: viewType, isRM_table: isRM_table  })
    } catch (error) {
      setSnackBarObject({ snackbarOpen: true, message: error.message, type: 'error' })
    } finally {
      setExportLoader(false);
    }
  };

  const exportORTrendChart = async (granularity) => {
    setExportLoader(true);
    try {
      await handleWeeklyTrendExport({ filterValues: orLossTreeSelectedFilters, view: viewType, heat_map_filters: {filter: granularity} })
    } catch (error) {
      setSnackBarObject({ snackbarOpen: true, message: error.message, type: 'error' })
    } finally {
      setExportLoader(false);
    }
  };
  
  const exportHeatmap = async (granularity) => {
    setExportLoader(true);
    try {
      await handleOrLossHeatmapExport({ filterValues: orLossTreeSelectedFilters, view: viewType, heat_map_filters: {filter: granularity} })
    } catch (error) {
      setSnackBarObject({ snackbarOpen: true, message: error.message, type: 'error' })
    } finally {
      setExportLoader(false);
    }
  };

  if (state?.rmpmFilters.length === 0 || state?.rmpmFilters === undefined) {
    return (
      <div className={styles.loaderContainer}>
        <CircularProgress />
      </div>
    );
  }

  const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    "& .MuiToggleButtonGroup-grouped": {
      fontSize: "12px",
      fontWeight: 400,
      padding: "3px 5px",
      textTransform: "none",
      width: "9rem",
      color: "#2B2B2B",
      "&:hover": {
        background: "transparent",
      },
    },
    "& .MuiToggleButtonGroup-grouped.Mui-selected": {
      background: "#1F36C7",
      color: "white",
      "&:hover": {
        background: "#1F36C7",
        color: "white",
      },
    },
  }));

  const weekColFilter = (label, data) => {};

  const selectDefaultValue = (label) => {
    if (alertType !== 'Or Loss Tree') return null;

    if (label === 'Data UOM') {
      return 'CLD';
    } else if (label === 'Year') {
      const currentYear = String(dayjs().year());
      return [currentYear];
    }

    return null;
  }

  const activeFilterKey = viewType === "MAI View" ? "maiFilters" : "rmpmFilters";
  const activeFilters = state[activeFilterKey] || [];

  const renderSelectableFilters = () => (
    activeFilters
      .filter((filter) => filter.isFavourite)
      .map((filter) => (
        <ListItem key={filter.label} className={styles.listCustomClass}>
          <Multiselectwithsearchinput
            key={filter.label}
            label={filter.label}
            options={state.rmpmFiltersOption[filter.label] ?? filter.data}
            filterValues={filter.selectedData}
            handleFilter={(label, values) =>
              handleMultiSelectChange(
                label,
                values,
                viewType === "MAI View",
              )
            }
            disabled={false}
            type={alertType}
            isMultiSelect={filter.isMultiSelect}
            defaultValue={selectDefaultValue(filter.label)}
          />
        </ListItem>
      ))
  );

  const handleChipDelete = (filter, subIndex) => {
    handleMultiSelectChange(
      filter.label,
      removeSelectionAtIndex(filter.selectedData, subIndex),
      viewType === "MAI View",
    );
  };

  const renderSelectedFilterChips = () => (
    activeFilters
      .filter((filter) => !filter.isFavourite && filter.selectedData?.length)
      .map((filter, index) => {
        const selectedDataArray = Array.isArray(filter.selectedData)
          ? filter.selectedData
          : [filter.selectedData];

        return (
          <div key={index}>
            {selectedDataArray.map((selectedValue, subIndex) => (
              <Chip
                key={`${index}-${subIndex}`}
                label={selectedValue}
                onDelete={() => handleChipDelete(filter, subIndex)}
              />
            ))}
          </div>
        );
      })
  );

  const handleMoreFiltersChange = (filters) => {
    setState((prev) => ({
      ...prev,
      [activeFilterKey]: filters,
    }));

    if (alertType === "Or Loss Tree" && viewType !== "MAI View") {
      const mappedFilters = filters.map((filter) => ({
        label: filter.label,
        selectedData: filter.selectedData,
      }));
      setOrLossTreeSelectedFilters(mappedFilters);
      setFilterChangeCount((count) => count + 1);
    }
  };

  const renderActiveTable = () => (
    viewType === "MAI View" || viewType === "Absolute" ? (
      <TableComponent2
        data={
          viewType === "MAI View"
            ? state.maiFilteredData
            : viewType === "Absolute"
              ? state.filteredData
              : ""
        }
        columnList={
          viewType === "MAI View"
            ? state.maiDetailsColumns
            : viewType === "Absolute"
              ? state.defaultColumns
              : ""
        }
        type={alertType}
        viewType={viewType}
        loading={state.loading}
      />
    ) : (
      <TableComponent
        data={state.filteredData}
        supplierInsights={state.supplierInsights}
        vendorQCIndex={state.vendorQCIndex}
        overduePO={state.overduePO}
        leadTimeAnalysis={state.leadTimeAnalysis}
        sobCompliance={state.sobCompliance}
        columnList={state.columnList}
        viewType={viewType}
        type={alertType}
        loading={state.loading}
        exportORTrendChart={exportORTrendChart}
        exportHeatmap={exportHeatmap}
      />
    )
  );

  const renderMoreFiltersDrawer = () => (
    uiConfig.showMoreFilters && state.filterDrawer && (
      <MoreFilters
        anchor="right"
        filterDrawer={state.filterDrawer}
        handleFilterDrawer={handleFilterDrawer}
        rmpmFilters={activeFilters}
        options={
          viewType !== "MAI View" ? state.rmpmFiltersOption : undefined
        }
        setRmpmFilters={handleMoreFiltersChange}
        username={activeAccount.username}
        type={alertType}
        limit={10}
      />
    )
  );

  const renderOrLossTreeContent = () => (
    <>
      {uiConfig.showKPI && (
        <div className={styles.kpiContainer}>
          <AlertRmpmKpi
            KPIObj={kpiData}
            isMultipleWeek={isWeekMultiple}
            type={alertType}
          />
        </div>
      )}

      <div
        className={clsx(styles.rmpmContainer, {
          [styles.fullHeight]: uiConfig.containerClass === "fullHeight",
          [styles.fullHeightWeekly]:
            uiConfig.containerClass === "fullHeightWeekly",
          [styles.fullHeightSupplierInsights]:
            uiConfig.containerClass === "fullHeightSupplierInsights",
        })}
      >
        <div className={styles.rmpmTableBody}>
          <div className={styles.filters}>
            {/* Regular filters */}
            {renderSelectableFilters()}

            {viewType === "MAI View" && (
              <ListItem key="weeks" className={styles.listCustomClass}>
                <Multiselectwithsearchinput
                  key="Week"
                  label="Week"
                  options={state.weekNum}
                  filterValues={[]}
                  handleFilter={(label, values) => weekColFilter(label, values)}
                  disabled={false}
                  type={alertType}
                />
              </ListItem>
            )}

            {/* Action buttons (reset and overflow menu) */}
            <div
              style={{
                paddingLeft: "5px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src={resetBtn}
                onClick={resetFilter}
                alt="Reset Filters"
                style={{ cursor: "pointer" }}
              />

              <OverflowMenu
                menuItems={[
                  {
                    label: "More Filters",
                    icon: Filters,
                    onClick: handleFilterDrawer,
                  },
                  {
                    label: "Export",
                    icon: Excel,
                    onClick: handleExcelPopup,
                  },
                ]}
              />
            </div>
          </div>

          <div className={styles.filtersBtnContainer}>
            <Box className={styles.chipContainer}>
              {renderSelectedFilterChips()}
            </Box>
          </div>

          {uiConfig.showToogleButton && (
            <div className={styles.toggleBtnContainer}>
              <StyledToggleButtonGroup
                color="primary"
                value={viewType}
                exclusive
                onChange={handleToggleBtnChange}
                aria-label="Platform"
                size="small"
              >
                {uiConfig.toggleBtn.map((details) => (
                  <ToggleButton key={details} value={details}>
                    {details}
                  </ToggleButton>
                ))}
              </StyledToggleButtonGroup>
            </div>
          )}

          {renderActiveTable()}
        </div>
      </div>

      {snackBarObject.snackbarOpen && (
        <SnackBarMessage
          type={snackBarObject.type}
          snackbarMessage={snackBarObject.message}
          snackbarOpen={snackBarObject.snackbarOpen}
          setSnackbarOpen={closeSnackbar}
        />
      )}
      {/* Export Loader */}
      {exportLoader && <ExportLoader type={'Or Loss Tree'}/>}

      {uiConfig.showFooter && (
        <div className={styles.rmpmFooter}>
          <div className={styles.btnContainer}>
            {uiConfig.showReviewCart && (
              <CustomButton
                label="Review Cart"
                type="cart"
                disabled={selectedRecomLength === 0}
                onClick={toggleDrawer(true)}
              />
            )}
            <CustomButton
              label="Submit"
              count={selectedRecomLength}
              type="submit"
              disabled={selectedRecomLength === 0}
              onClick={handleSubmit}
            />
          </div>
        </div>
      )}

      {uiConfig.showActionPopup && (
        <ActionPopup
          open={state.openPopup}
          onClose={() => setState((prev) => ({ ...prev, openPopup: false }))}
          type={alertType}
        />
      )}

      {uiConfig.showCustomizeColumns && state.isDrawerOpen && (
        <CustomiseColumns
          anchor="right"
          columns={
            viewType === "MAI View" ? state.maiDetailsColumns : state.columnList
          }
          isOpen={state.isDrawerOpen}
          handleCustomizeDrawer={handleCustomizeDrawer}
          columnCustomization={columnCustomization}
          username={activeAccount.username}
          type={alertType}
        />
      )}

      {renderMoreFiltersDrawer()}

      {state.openExcelPopup && (
        <ORLTExcelPopup
          open={state.openExcelPopup}
          onClose={() => setState((prev) => ({ ...prev, openExcelPopup: false }))}
          handleExportToExcel={handleExportToExcel}
        />
      )}

      {uiConfig?.showChatBot === <ChatBot />}
    </>
  );

  return renderOrLossTreeContent();
};

ORLossTree.propTypes = {
  alertType: PropTypes.oneOf([
    "RMPM Alert",
    "Norm Alert",
    "Contract Alert",
    "Contract PR2 Alert",
    "Agile OR",
    "Weekly OR",
    "Or Loss Tree",
    "Supplier Insights",
    "FG Coverage",
    "Manpower Shortage",
  ]).isRequired,
};

export default React.memo(ORLossTree);
